/**
 * @author Jacek Jankowski / http://grey-eminence.org/
 * http://3dit.bordeaux.inria.fr/testbed.html#navigation_Map_Navigation
 */

// It is an adaptation of the three.js OrbitControls class to map environments
'use strict';

import * as THREE from 'three';
import { Vector2 } from 'three';

import { isDefined } from '../../../utils/variables';
import { ElementObj } from '../../elements/element-obj';
import { Ground } from '../../ground';
import { publish, SubscriptionTopic } from '../../messaging/pubsub';
import {
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../../utilities/input-event/input-event-data-extractor';
import { ViewSettings } from '../../view-settings';
import { SkyviewOrthographicCamera } from '../skyview-orthographic-camera';
import { CameraControllerInterface, CameraControllerStateType } from '../types';
import { SceneManager } from './../../scene-manager';
import {
  isBoundingBoxWithinValidRange,
  shouldUpdateSceneOrigo,
  updateSceneOrigo,
} from './focus-utils';

export class OrthotileCameraController implements CameraControllerInterface {
  private state: CameraControllerStateType;
  private panStart: THREE.Vector2;
  private focusedAsset: THREE.Object3D | undefined;

  private transitionEndTime: number = Date.now();
  constructor(private camera: SkyviewOrthographicCamera) {
    this.state = CameraControllerStateType.Idle;

    this.panStart = new THREE.Vector2();
  }

  /**
   *  Set the controller state based on the number of touches
   */
  onTouchStart(input: TouchInputData) {
    if (input.touches.length === 1) {
      this.state = CameraControllerStateType.Panning;

      const intersections = Ground.instance.pickPlane(
        this.camera.getRay(input.center!.position.x, input.center!.position.y),
      );
      if (intersections.length > 0) {
        this.panStart = new Vector2(intersections[0].point.x, intersections[0].point.y);
      }
    } else {
      this.state = CameraControllerStateType.Idle;
    }

    return false;
  }

  /**
   *  Transform the camera based on the ground intersection delta
   */
  onTouchMove(input: TouchInputData) {
    if (
      this.state !== CameraControllerStateType.Panning &&
      (!isDefined(input.touchLength) || input.touchLength.delta === 0)
    ) {
      return false;
    }

    const intersections = Ground.instance.pickPlane(
      this.camera.getRay(input.center!.position.x, input.center!.position.y),
    );

    if (intersections.length > 0) {
      const delta = this.panStart
        .clone()
        .sub(new Vector2(intersections[0].point.x, intersections[0].point.y));
      this.camera.move(new THREE.Vector3(delta.x, delta.y, 0));
    }

    if (input.touchLength && input.touchLength.delta) {
      this.camera.zooming(-input.touchLength.delta * 1000);
    }

    return true;
  }

  /**
   *  Reset the camera control state
   */
  onTouchEnd(input: TouchInputData) {
    this.state = CameraControllerStateType.Idle;
    this.onTransitionStop();

    return false;
  }

  /**
   *  Set the controller state based on the mouse button pressed
   */
  onMouseDown(input: MouseInputData) {
    if (
      input.button.type === MouseButtonType.LeftButton ||
      input.button.type === MouseButtonType.MiddleButton
    ) {
      this.state = CameraControllerStateType.Panning;

      const intersections = Ground.instance.pickPlane(
        this.camera.getRay(input.pointer.position.x, input.pointer.position.y),
      );
      if (intersections.length > 0) {
        this.panStart = new Vector2(intersections[0].point.x, intersections[0].point.y);
      }
    }

    return false;
  }

  /**
   *  Transform the camera based on the ground intersection delta
   */
  onMouseMove(input: MouseInputData) {
    if (this.state !== CameraControllerStateType.Panning) {
      return false;
    }
    const intersections = Ground.instance.pickPlane(
      this.camera.getRay(input.pointer.position.x, input.pointer.position.y),
    );

    if (intersections.length > 0) {
      const delta = this.panStart
        .clone()
        .sub(new Vector2(intersections[0].point.x, intersections[0].point.y));
      this.camera.move(new THREE.Vector3(delta.x, delta.y, 0));
    }

    this.unfocusAsset();

    return true;
  }

  /**
   *  Reset the camera control state
   */
  onMouseUp(input: MouseInputData) {
    this.state = CameraControllerStateType.Idle;
    this.onTransitionStop();

    return false;
  }

  /**
   *  Zoom based on the input wheel value
   */
  onMouseWheel(input: MouseInputData) {
    this.camera.zooming(-input.wheel.delta, input.pointer.position);

    this.transitionEndTime = Date.now();

    return true;
  }

  unfocusAsset(): void {
    if (this.focusedAsset) {
      publish(SubscriptionTopic.SkyviewAssetFocusChanged, { assetUuid: undefined });
    }

    this.focusedAsset = undefined;
  }

  /**
   *  Focus the camera on an asset.
   */
  focusOnAsset(asset: ElementObj | undefined): void {
    // Do not focus on invisible assets.
    if (asset?.visible !== true) {
      return;
    }

    publish(SubscriptionTopic.SkyviewAssetFocusChanged, { assetUuid: asset.uuid });

    const bb = asset.generateBoundingBox(true);

    if (!isBoundingBoxWithinValidRange(bb)) {
      throw new Error(
        'Error focusing on asset. The asset is not located within the valid range of the projects coordinate system.',
      );
    }

    const hasToUpdateSceneOrigo = shouldUpdateSceneOrigo(bb, ViewSettings.instance.isCadViewer);
    if (hasToUpdateSceneOrigo) {
      updateSceneOrigo(bb.min.clone());
    }

    // Call after timeout so the asset has time to recalculate its position with pubsub event.
    setTimeout(
      () => {
        this.internalFocusOnBox(asset.generateBoundingBox(true));
        this.focusedAsset = asset;
      },
      hasToUpdateSceneOrigo ? 200 : 0,
    );
  }
  /**
   *  Return the asset currently in focus
   */
  getFocusedAsset() {
    return this.focusedAsset;
  }

  /**
   *  Focus around a bounding box
   */
  focusOnBox(bb: THREE.Box3) {
    this.internalFocusOnBox(bb);
  }

  /**
   *  Fetches the geodata bounderies and focuses on it
   */
  focusOnScene() {
    const activeScene = SceneManager.instance.getActiveScene();
    if (!activeScene) {
      return;
    }
    const bb = activeScene.getGeodataBounderies();
    if (!bb) {
      console.log(`There is not bb defined to focus on.`);
      return;
    }
    try {
      this.internalFocusOnBox(bb);
    } catch (err) {
      console.log(`Error when focusing on scene geodata: ${JSON.stringify(err, null, 2)}`);
      this.state = CameraControllerStateType.Idle;
    }
  }

  /**
   *  Returns true if the camera controller has been in transition within the last half minute
   */
  isTransitioning() {
    return (
      this.state !== CameraControllerStateType.Idle || Date.now() - this.transitionEndTime < 500
    );
  }

  private onTransitionStop() {
    this.transitionEndTime = Date.now();
  }

  /**
   *  Focuses the camera viewport on a Box3.
   *  Handles transition state and publishes the camera moved event.
   */
  private internalFocusOnBox(bb: THREE.Box3) {
    this.camera.focusOnBox(bb, 0, () => {
      this.state = CameraControllerStateType.Idle;
      this.onTransitionStop();

      publish(SubscriptionTopic.OnCameraMoved, { camera: this.camera });
    });

    this.state = CameraControllerStateType.Tweening;
  }
}
